import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBlogPostBySlug, getRelatedPosts } from '../../data/blogDataLoader';
import { trackPageView } from '../../utils/analytics';
import './BlogPage.css';
import { CTA } from '../../components';
import Callbackpopup from '../callbackpopup/Callbackpopup';

const BlogPage = () => {
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [blogPost, setBlogPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { slug } = useParams();
  const location = useLocation();

  // Load blog post data
  useEffect(() => {
    const loadBlogData = async () => {
      setIsLoading(true);
      try {
        const post = await getBlogPostBySlug(slug);
        const related = await getRelatedPosts(slug);
        setBlogPost(post);
        setRelatedPosts(related);
        // Mark blog post as loaded for pre-rendering
        document.body.setAttribute('data-blog-loaded', 'true');
      } catch (error) {
        console.error('Error loading blog post:', error);
        setBlogPost(null);
        document.body.setAttribute('data-blog-loaded', 'true');
      } finally {
        setIsLoading(false);
      }
    };
    loadBlogData();
  }, [slug]);

  // Track page view on route change
  useEffect(() => {
    if (blogPost) {
      const pageTitle = `${blogPost.title} | SnagEasy Blog`;
      // Track page view with the correct title
      trackPageView(location.pathname + location.search, pageTitle);
    }
  }, [blogPost, location.pathname, location.search]);

  if (isLoading) {
    return (
      <div className="blog-page">
        <div className="blog-loading">
          <h1>Loading...</h1>
          <p>Fetching blog post...</p>
        </div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="blog-page">
        <div className="blog-not-found">
          <h1>Blog Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }
  const siteUrl = 'https://www.snageasy.in';
  
  // Check if blog slug matches any landing page
  const canonicalUrl = `${siteUrl}/blog/${blogPost.slug}`;
  const pageUrl = `${siteUrl}/blog/${blogPost.slug}`;
  const imageUrl = blogPost.featuredImage || (siteUrl + '/default-blog-image.png');
  // Some posts' own title field already ends with "| SnagEasy" (authored that
  // way in blogData.js) — appending it unconditionally doubled the suffix on
  // those posts' rendered <title> tag. Only add it if it isn't already there.
  const pageTitle = !blogPost.title
    ? 'Blog Post | SnagEasy'
    : /\|\s*SnagEasy\s*$/i.test(blogPost.title)
      ? blogPost.title
      : `${blogPost.title} | SnagEasy`;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const openCallbackPopup = () => {
    setPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeCallbackPopup = () => {
    setPopupOpen(false);
    document.body.style.overflow = 'auto';
  };
  const isoDate = (dateString) => {
    const d = new Date(dateString);
    return d.toISOString();
  };

  // JSON-LD structured data for Article
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${pageUrl}#blogpost`,
    "headline": blogPost.title,
    "description": blogPost.metaDescription || blogPost.excerpt,
    "image": [imageUrl],
    "author": {
      "@type": "Person",
      "name": blogPost.author || "SnagEasy Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SnagEasy – N S Mountains LLP",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo512.png`
      }
    },
    "datePublished": isoDate(blogPost.publishDate),
    "dateModified": isoDate(blogPost.publishDate),
    "keywords": (blogPost.tags || []).join(", "),
    "isPartOf": {
      "@type": "Blog",
      "@id": `${siteUrl}/blog`,
      "name": "SnagEasy Blog"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    }
  };  

  return (
    <div className="blog-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />

        {/* Basic meta */}
        <meta name="description" content={blogPost.metaDescription || blogPost.excerpt} />
        <meta name="keywords" content={(blogPost.tags || []).join(', ')} />

        {/* Open Graph */}
        <meta property="og:title" content={blogPost.title} />
        <meta property="og:description" content={blogPost.metaDescription || blogPost.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="N S Mountains LLP (SnagEasy)" />
        <meta property="article:published_time" content={isoDate(blogPost.publishDate)} />
        {blogPost.tags && blogPost.tags.map((t, i) => (
          <meta key={i} property="article:tag" content={t} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogPost.title} />
        <meta name="twitter:description" content={blogPost.metaDescription || blogPost.excerpt} />
        <meta name="twitter:image" content={imageUrl} />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

        {/* Preload featured image for faster social preview */}
        <link rel="preload" as="image" href={imageUrl} fetchPriority="high" />
      </Helmet>

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-container">
          <div className="blog-hero-content">
            <div className="blog-meta">
              <span className="blog-category">{blogPost.category}</span>
              <span className="blog-date">{formatDate(blogPost.publishDate)}</span>
              <span className="blog-read-time">{blogPost.readTime}</span>
            </div>
            <h1 className="blog-title">{blogPost.title}</h1>
            <p className="blog-excerpt">{blogPost.excerpt}</p>
            <div className="blog-author">
              <div className="author-info">
                <div className="author-avatar" aria-hidden>
                  <span>{blogPost.author.charAt(0)}</span>
                </div>
                <div className="author-details">
                  <span className="author-name">{blogPost.author}</span>
                  <span className="author-role">Home Inspection Expert</span>
                </div>
              </div>
            </div>
          </div>
          <div className="blog-hero-image">
            <img src={imageUrl} alt={blogPost.title} loading="lazy" />
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="blog-content">
        <div className="blog-content-container">
          <article className="blog-article" itemScope itemType="https://schema.org/Article">
            <meta itemProp="headline" content={blogPost.title} />
            <meta itemProp="author" content={blogPost.author} />
            <meta itemProp="datePublished" content={isoDate(blogPost.publishDate)} />
            <div
              className="blog-article-content"
              dangerouslySetInnerHTML={{ __html: blogPost.content }}
            />

            {/* Tags */}
            <div className="blog-tags">
              <h4>Tags:</h4>
              <div className="tags-list">
                {blogPost.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="blog-sidebar" aria-label="Blog sidebar">
            <div className="sidebar-widget">
              <h3>Quick Contact</h3>
              <p>Need help with your home inspection?</p>
              <button className="cta-button" onClick={openCallbackPopup}>Get Expert Advice</button>
            </div>

            <div className="sidebar-widget">
              <h3>Our Services</h3>
              <ul className="services-list">
                <li>Home Inspection</li>
                <li>Flat Inspection</li>
                <li>RERA Compliance</li>
                <li>Quality Control</li>
                <li><a href="/home-inspection-pune">Home Inspection in Pune</a></li>
                <li><a href="/home-inspection-mumbai">Home Inspection in Mumbai</a></li>
                <li><a href="/home-inspection-bangalore">Home Inspection in Bangalore</a></li>
                <li><a href="/home-inspection-chennai">Home Inspection in Chennai</a></li>
                <li><a href="/professional-home-inspection-demo">Home Inspection Demo</a></li>
                <li><a href="/thermal-inspection">Thermal Inspection</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <div className="related-posts-container">
            <h2>Related Articles</h2>
            <div className="related-posts-grid">
              {relatedPosts.map((post) => (
                <div key={post.id} className="related-post-card">
                  <img src={post.featuredImage} alt={post.title} />
                  <div className="related-post-content">
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <a href={`/blog/${post.slug}`} className="read-more">Read More</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTA />
      {isPopupOpen && <Callbackpopup onClose={closeCallbackPopup} cardItem="Get a Call Back from Expert" />}
    </div>
  );
};

export default BlogPage;
